import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Users, Layout, Bell, MapPin, Target, Video, MessageSquare, FileText, Image as ImageIcon } from 'lucide-react';

export default function OrganizationSettings() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [adminAccount, setAdminAccount] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    loadOrganization();
    loadAdminAccount();
  }, [orgId]);

  const loadOrganization = async () => {
    try {
      const res = await axios.get(`${API}/organizations/${orgId}`);
      setOrg(res.data);
    } catch (error) {
      toast.error('載入機構失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadAdminAccount = async () => {
    try {
      const res = await axios.get(`${API}/organizations/${orgId}/admin`);
      if (res.data && res.data.username) {
        setAdminAccount({ 
          username: res.data.username, 
          password: '' 
        });
      }
    } catch (error) {
      console.log('No admin account yet');
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData);
      
      handleUpdate(field, res.data.url);
      toast.success('圖片已上傳');
    } catch (error) {
      toast.error('上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (field, value) => {
    try {
      await axios.put(`${API}/organizations/${orgId}`, { [field]: value });
      setOrg({ ...org, [field]: value });
      toast.success('已更新');
    } catch (error) {
      toast.error('更新失敗');
    }
  };

  const handleCreateAdmin = async () => {
    if (!adminAccount.username || !adminAccount.password) {
      toast.error('請填寫用戶名稱和密碼');
      return;
    }

    try {
      await axios.post(`${API}/organizations/${orgId}/admin`, adminAccount);
      toast.success('機構管理員已創建');
      setAdminAccount({ ...adminAccount, password: '' });
      loadAdminAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || '創建失敗');
    }
  };

  if (loading) return <div className="p-8">載入中...</div>;
  if (!org) return <div className="p-8">機構不存在</div>;

  const tabs = [
    { key: 'basic', label: '基本設定', icon: Layout },
    { key: 'users', label: '用戶管理', icon: Users }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2" size={16} />
            返回後台
          </Button>
          <h1 className="text-3xl font-bold text-gray-800">{org.name} - 機構管理</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-6">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex overflow-x-auto border-b">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-6 py-4 font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.key
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'basic' && (
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">基本資訊</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">機構名稱</label>
                  <Input
                    value={org.name}
                    onChange={(e) => setOrg({ ...org, name: e.target.value })}
                    onBlur={(e) => handleUpdate('name', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">機構ID</label>
                  <Input value={org.organization_id} disabled />
                </div>
              </div>
            </div>

            {/* Logo and Theme */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">外觀設定</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Logo</label>
                  <div className="flex items-center gap-4">
                    {org.logo_url && (
                      <img src={org.logo_url} alt="Logo" className="h-16 w-16 object-contain border rounded" />
                    )}
                    <label className="px-4 py-2 bg-gray-100 rounded cursor-pointer hover:bg-gray-200">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'logo_url')}
                        className="hidden"
                        disabled={uploading}
                      />
                      {uploading ? '上傳中...' : '上傳Logo'}
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">背景圖片</label>
                  <div className="flex items-center gap-4">
                    {org.background_image && (
                      <img src={org.background_image} alt="Background" className="h-24 w-32 object-cover border rounded" />
                    )}
                    <label className="px-4 py-2 bg-gray-100 rounded cursor-pointer hover:bg-gray-200">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'background_image')}
                        className="hidden"
                        disabled={uploading}
                      />
                      {uploading ? '上傳中...' : '上傳背景圖'}
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">主題色</label>
                  <div className="flex items-center gap-4">
                    <Input
                      type="color"
                      value={org.theme_color || '#FF6B35'}
                      onChange={(e) => handleUpdate('theme_color', e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      value={org.theme_color || '#FF6B35'}
                      onChange={(e) => setOrg({ ...org, theme_color: e.target.value })}
                      onBlur={(e) => handleUpdate('theme_color', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Account */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">機構管理員帳號</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">用戶名稱</label>
                  <Input
                    value={adminAccount.username}
                    onChange={(e) => setAdminAccount({ ...adminAccount, username: e.target.value })}
                    placeholder="設定管理員用戶名"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">密碼</label>
                  <Input
                    type="password"
                    value={adminAccount.password}
                    onChange={(e) => setAdminAccount({ ...adminAccount, password: e.target.value })}
                    placeholder="設定管理員密碼"
                  />
                </div>
                <Button onClick={handleCreateAdmin} className="w-full">
                  創建/更新管理員帳號
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">用戶管理</h2>
            <p className="text-gray-600">此機構的用戶管理功能開發中...</p>
          </div>
        )}
      </div>
    </div>
  );
}
